import { useContext, useEffect, useState, useRef } from "react";

import {
  AddTask,
  ArrowBack,
  ArrowForward,
  PublishedWithChanges,
} from "@mui/icons-material";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Link,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import {
  createBulletin,
  getBulletin,
  getBulletinDraft,
  patchBulletinId,
  BulletinPatch,
} from "@rubi/api-generated";
import { colors, PotentialDuplicateWarningDialog } from "@rubi/ui";
import { GoogleMapsProvider } from "@rubi/ui";
import { toast } from "@rubi/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { useAlertCrimeTypesQuery } from "@/api/loadAlerts";
import { mailTo, stripPhoneNumberFormatting } from "@/common/utils";
import { AuthStateContext } from "@/contexts/AuthContext";
import {
  AlertMode,
  BulletinPatchPayload,
  ITag,
  IUserPermission,
} from "@/types";
import { DisabledComponentDialog } from "@/components/DisabledComponent";
import { parseUserPermissions } from "@/components/UserManagement/utils";
import "@/components/alerts/alerts.scss";
import {
  BULLETIN_TYPE,
  LOCATION_CLASSIFICATION,
  TIME_TYPE,
  alertSteps,
  defaultAlertProperties,
  defaultErrors,
  generateValidationCriteria,
} from "@/components/alerts/constants";
import {
  AlertViewContext,
  SingleAlertContext,
} from "@/components/alerts/context";
import { isContactEmpty } from "@/components/alerts/utils";

import CancelEditDialog from "./CancelEditDialog";
import { DetailsStep, DetailsStepHandle } from "./DetailsStep";
import { DisplayAlert } from "./DisplayAlert";
import StepNavigation from "./StepNavigation";
import UploadPdf from "./UploadPdf";
import { UploadStep } from "./UploadStep";

interface UpdateAlertProps {
  mode: AlertMode;
}

const UpdateAlertContent = ({ mode }: UpdateAlertProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation(["bulletin/create"]);
  const {
    alertProperties,
    setAlertProperties,
    setAlertProperty,
    setErrors,
    refreshAlertData,
  } = useContext(SingleAlertContext);

  const { setUpdatedItems, policeServices } = useContext(AlertViewContext);
  const { data: crimeTypes = [] } = useAlertCrimeTypesQuery();
  const { currentUser, currentUserGroups } = useContext(AuthStateContext);

  const { id } = useParams();

  const [validationAttempted, setValidationAttempted] =
    useState<boolean>(false);

  const [isReadOnlyDialogOpen, setIsReadOnlyDialogOpen] = useState(false);
  const [alertPermissions] = useState(
    currentUser &&
      parseUserPermissions(currentUser?.permissions as IUserPermission[]),
  );

  const readOnly = alertPermissions
    ? !alertPermissions.canCreateBulletin
    : false;

  useEffect(() => {
    if (readOnly) {
      setIsReadOnlyDialogOpen(true);
    }
  }, [readOnly]);

  function updateState(key, value) {
    setAlertProperty(key, value);
  }
  const handleUpdate = (key: any) => {
    return (value: any) => {
      updateState(key, value);
    };
  };

  const ReadOnlyDialog = () => {
    if (!readOnly) return null;
    return (
      <DisabledComponentDialog
        isDialogOpen={isReadOnlyDialogOpen}
        handleClose={() => navigate(`/rubialert/alerts`)}
        title={t("read_only_title")}
        message={
          <Typography>
            Please contact{" "}
            <Link
              rel="noopener noreferrer"
              target="_blank"
              href={mailTo({
                email: "support@tryrubicon.com",
                subject: "Requesting Access to Create Bulletins",
                body: `Hello Rubicon Support Team, 

                I would like to request access to create bulletins.

                Thank you. 

                Sincerely,
                
                ${currentUser?.first_name} ${currentUser?.last_name} 
                ${currentUser?.email}`,
              })}
              color="secondary"
            >
              support@tryrubicon.com
            </Link>{" "}
            to request access.
          </Typography>
        }
      />
    );
  };

  const {
    policeService,
    type,
    associatedCrimes,
    occurrenceNumbers,
    incidentTimes,
    issuedTime,
    description,
    operationalArea,
    approvedBy,
    approvedByBadge,
    classification,
    bulletinId,
    contacts,
    rawAddresses,
    evidence_files,
    isDraft,
    pdf,
    originalLanguage,
    descriptionFr,
  } = alertProperties;

  useEffect(() => {
    if (mode === "edit" && !bulletinId && id) {
      setAlertProperty("bulletinId", id);
      refreshAlertData(id);
    }
  }, [mode, bulletinId, id, setAlertProperty, refreshAlertData]);
  // const [issuedTime, setIssuedTime] = useState<Dayjs | null>(null);
  const [openCancelEdit, setOpenCancelEdit] = useState<boolean>(false);
  const [isUploadPdfModalOpen, setIsUploadPdfModalOpen] =
    useState<boolean>(true);
  const [duplicateBulletins, setDuplicateBulletins] = useState<
    { id: number; occurrence_number: string[] }[]
  >([]);
  const [openDuplicateDialog, setOpenDuplicateDialog] =
    useState<boolean>(false);
  const prevOccurrenceNumbers = useRef<string[]>(occurrenceNumbers);
  const [evidenceFilesLoading, setEvidenceFilesLoading] =
    useState<boolean>(false);
  const [isAttachmentSyncing, setIsAttachmentSyncing] =
    useState<boolean>(false);

  const [cleared, setCleared] = useState<boolean>(false);
  const queryClient = useQueryClient();

  const steps = alertSteps;
  const [activeStep, setActiveStep] = useState<0 | 1 | 2>(0 as 0 | 1 | 2);
  const [searchParams] = useSearchParams();

  const setBulletinID = (id: number) => {
    return new Promise((resolve) => {
      setAlertProperty("bulletinId", id);
      resolve(id);
    });
  };

  const [imageUploadedFlag, setImageUploadedFlag] = useState<boolean>(
    Object.keys(evidence_files).length > 0,
  );
  const [openImageCheckDialog, setOpenImageCheckDialog] =
    useState<boolean>(false);

  // Check if images have been uploaded
  useEffect(() => {
    if (Object.keys(evidence_files).length > 0) {
      setImageUploadedFlag(true);
    }
  }, [evidence_files]);

  // When bulletin data loads (edit mode), sync the ref so pre-existing
  // occurrence numbers don't trigger a duplicate check.
  useEffect(() => {
    prevOccurrenceNumbers.current = occurrenceNumbers;
  }, [bulletinId]);

  // Check for duplicate occurrence numbers when a new one is added
  useEffect(() => {
    const prev = prevOccurrenceNumbers.current;
    const newNums = occurrenceNumbers.filter((n) => !prev.includes(n));
    prevOccurrenceNumbers.current = occurrenceNumbers;

    if (newNums.length === 0) return;

    newNums.forEach(async (num) => {
      try {
        const res = await getBulletin({
          query: {
            start: 0,
            amount: 20,
            is_draft: false,
            occurrence_number: num,
          },
        });
        const matches = (res.data?.items ?? []).filter(
          (b: any) =>
            b.id !== bulletinId &&
            Array.isArray(b.occurrence_number) &&
            b.occurrence_number.some(
              (n: string) => n.toLowerCase() === num.toLowerCase(),
            ),
        );
        if (matches.length > 0) {
          setDuplicateBulletins(
            matches.map((b: any) => ({
              id: b.id,
              occurrence_number: b.occurrence_number,
            })),
          );
          setOpenDuplicateDialog(true);
        }
      } catch (err) {
        console.error("Error checking for duplicate occurrence numbers:", err);
      }
    });
  }, [occurrenceNumbers]);

  const createBulletinResource = async () => {
    const res = await createBulletin({ body: {} });
    if (!res.data) {
      throw res.error ?? new Error("Failed to create bulletin");
    }
    await setBulletinID(res.data.id);
    return res.data.id;
  };

  const handleSaveDraft = async () => {
    if (bulletinId) {
      await saveBulletin(true);
    } else {
      const newBulletinId = await createBulletinResource();
      await saveBulletin(true, newBulletinId);
    }
  };

  /**
   * Save the alert to the database
   * should use only the alertProperties object
   */
  const saveBulletin = async (isDraft = false, id = bulletinId) => {
    const timezonedIssuedDate: string | null =
      (issuedTime !== null &&
        issuedTime.isValid() &&
        issuedTime.toISOString()) ||
      null;

    if (!window.google?.maps?.Geocoder) {
      // Throw rather than return: a silent return makes callers believe the
      // save succeeded when nothing was persisted.
      throw new Error("Google Maps API is not loaded yet");
    }
    const geocoder = new google.maps.Geocoder();
    const geocodedPromises = rawAddresses?.map((address) =>
      address ? geocoder.geocode({ placeId: address?.place_id }) : null,
    );
    const filteredContacts = contacts
      ?.filter((contact) => isContactEmpty(contact) === false)
      .map((contact) => ({
        ...contact,
        phone_number: contact?.phone_number
          ? stripPhoneNumberFormatting(contact?.phone_number)
          : contact?.phone_number,
      }));

    let data: BulletinPatchPayload = {
      type: type as BULLETIN_TYPE,
      occurrence_number: [...occurrenceNumbers],
      issued_time: timezonedIssuedDate as string,
      associated_crime: associatedCrimes.map((crime) => crime.crime),
      incident_times: incidentTimes,
      location: { addresses: [] },
      description: (description || null) as string,
      description_fr: (descriptionFr || null) as string,
      original_language: (originalLanguage || null) as string,
      issuing_police_service: {
        id: (policeService ? policeService.id : null) as number,
      },
      contacts: filteredContacts,
      operational_area: operationalArea as string,
      approved_by: approvedBy as string,
      approved_by_badge: approvedByBadge as string,
      classification: classification,
      is_draft: isDraft,
    };
    const results = await Promise.all(geocodedPromises);

    results.forEach((result, index) => {
      if (result?.results) {
        const geoResult = result?.results[0];
        // Match by index: the geocoder can return a place_id different from the
        // Autocomplete prediction's one (e.g. "Greater Sudbury"), and matching on
        // place_id then yields undefined. geocodedPromises is built 1:1 from
        // rawAddresses, so the index is always the reliable link.
        const rawResult = rawAddresses[index];

        let description = geoResult.formatted_address;
        let streetComponent = geoResult.address_components.filter((component) =>
          component.types.includes("street_number"),
        )[0];

        if (!streetComponent) {
          streetComponent = geoResult.address_components.filter((component) =>
            component.types.includes("intersection"),
          )[0];
        }
        const streetNumber = streetComponent ? streetComponent.long_name : "";

        if (!rawResult.structured_formatting) {
          description = rawResult.description;
        } else if (
          streetNumber &&
          !rawResult.structured_formatting.main_text.includes(streetNumber)
        ) {
          description = `${rawResult.structured_formatting.main_text} - ${geoResult.formatted_address}`;
        }
        data = {
          ...data,
          location: {
            addresses: [
              ...data.location.addresses,
              {
                description,
                place_id: geoResult.place_id,
                address_components: geoResult.address_components,
                geometry: {
                  lat: geoResult.geometry.location.lat(),
                  lng: geoResult.geometry.location.lng(),
                },
                location_type: geoResult.geometry.location_type,
                types: geoResult.types,
                plus_code: geoResult.plus_code,
                location_classification: rawResult.location_classification,
                other_location_classification:
                  rawResult.other_location_classification,
                structured_formatting: rawResult.structured_formatting,
              },
            ],
          },
        };
        setUpdatedItems((prev) => [...prev, data]);
      }
    });
    return patchBulletinId({
      path: { bulletin_id: Number(id) },
      body: data as unknown as BulletinPatch,
    }).then((res) => {
      if (res.error) {
        throw res.error;
      }
      setUpdatedItems((prev) => [...prev, data]);
      queryClient.invalidateQueries({
        queryKey: ["alerts"],
        refetchType: "all",
      });
      return res;
    });
  };

  const detailsStepRef = useRef<DetailsStepHandle>(null);

  const handleNext = async () => {
    setValidationAttempted(true);

    const validationMap = await generateValidationCriteria(
      alertProperties,
      policeServices,
    );
    const hasErrors = Object.keys(validationMap).some((key) => {
      if (key === "contactName") {
        const contactErrors = Object.values(validationMap[key]).some(
          (value) => value === true,
        );
        return contactErrors;
      }
      if (key === "addresses") {
        const addressErrors = validationMap[key].some(
          (address) => !address.isValid || !address.hasLocationType,
        );
        return addressErrors;
      }
      if (key === "incidentTimes") {
        // check if all incident times are valid
        const incidentTimeErrors = incidentTimes.some((incidentTime) => {
          if (
            incidentTime.time_type === "not_applicable" ||
            incidentTime.time_type === "unknown"
          ) {
            return false;
          }
          if (incidentTime.time_type === "range") {
            return !incidentTime.start_date || !incidentTime.end_date;
          }
          return !incidentTime.start_date;
        });
        validationMap[key] = incidentTimeErrors;
        return incidentTimeErrors;
      }
      return validationMap[key];
    });
    setErrors(validationMap);

    if (activeStep == 1 && !imageUploadedFlag) {
      setOpenImageCheckDialog(true);
    } else if (!hasErrors) {
      try {
        if (isDraft) {
          await handleSaveDraft();
        } else {
          await saveBulletin();
        }
      } catch (err) {
        console.error("Failed to save bulletin:", err);
        toast.error(t("error_message_save_failed"));
        return;
      }
      setActiveStep((prevActiveStep) => (prevActiveStep + 1) as 0 | 1 | 2);
    } else {
      // Use the ref to scroll to the first error
      detailsStepRef.current?.scrollToFirstError();
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => (prevActiveStep - 1) as 0 | 1 | 2);
  };

  const addUserEmailContact = () => {
    const userEmail = currentUser?.email;
    if (userEmail !== null) {
      handleUpdate("contacts")([
        {
          email: userEmail,
          phone_number: "",
          extension: "",
          name: "",
          badge_number: "",
          error: false,
        },
      ]);
    }
  };

  const setUsersPoliceService = () => {
    if (!policeServices || policeServices.length === 0) return;

    if (mode === "edit" && policeService) {
      handleUpdate("policeService")(policeService);
      return;
    }

    const userPoliceService = currentUserGroups?.find(
      (group) => group.is_police_service,
    );

    if (userPoliceService) {
      handleUpdate("policeService")(userPoliceService);
    } else {
      handleUpdate("policeService")(policeServices[0]);
    }
  };

  const resetState = () => {
    console.info("resetting state");
    setAlertProperties(defaultAlertProperties);
    // setIssuedTime(null);
    setActiveStep(0);
    setErrors(defaultErrors);
    addUserEmailContact();
    setCleared(true);
  };

  const loadFromQueryParams = async () => {
    // Check if we have a token parameter (new draft-based approach)
    const token = searchParams.get("token");

    if (token) {
      // Load data from bulletin draft API
      try {
        console.info("Loading bulletin draft from token:", token);
        const draftData = (
          await getBulletinDraft({
            path: { token },
          })
        ).data;
        if (!draftData) {
          toast.error("No draft data found for the provided token.");
          return;
        }
        // Map draft data to alert properties
        if (draftData.type) {
          setAlertProperty("type", draftData.type);
        }

        if (draftData.occurrence_number) {
          setAlertProperty("occurrenceNumbers", [draftData.occurrence_number]);
        }

        if (draftData.description) {
          setAlertProperty("description", draftData.description);
        }

        if (draftData.classification) {
          setAlertProperty("classification", draftData.classification);
        }

        // Handle location
        if (draftData.location?.google_maps_place) {
          const locationData = {
            place_id: draftData.location.google_maps_place.place_id,
            description: draftData.location.google_maps_place.formatted_address,
            structured_formatting: {
              main_text: draftData.location.google_maps_place.name,
              secondary_text:
                draftData.location.google_maps_place.formatted_address,
            },
            location_classification: LOCATION_CLASSIFICATION.INCIDENT_ADDRESS,
            other_location_classification: null,
            unit: draftData.location.unit || null,
          };
          setAlertProperty("rawAddresses", [locationData]);
        } else if ((draftData.location as any)?.raw_address) {
          const rawAddress = (draftData.location as any).raw_address as string;
          setAlertProperty("rawAddresses", [
            {
              place_id: "",
              description: rawAddress,
              structured_formatting: {
                main_text: rawAddress,
                secondary_text: "",
              },
              location_classification: LOCATION_CLASSIFICATION.INCIDENT_ADDRESS,
              other_location_classification: null,
              unit: draftData.location?.unit || null,
            },
          ]);
        }

        // Handle incident time
        if (draftData.time_type) {
          const tryParseDate = (dateStr: string | null): Date | null => {
            if (!dateStr) return null;
            const parsed = new Date(dateStr);
            return isNaN(parsed.getTime()) ? null : parsed;
          };

          let incidentTime: {
            time_type: string;
            start_date: Date | null;
            end_date: Date | null;
          };

          switch (draftData.time_type) {
            case TIME_TYPE.EXACT:
              incidentTime = {
                time_type: TIME_TYPE.EXACT,
                start_date: tryParseDate(draftData.time ?? null),
                end_date: null,
              };
              break;
            case TIME_TYPE.RANGE:
              incidentTime = {
                time_type: TIME_TYPE.RANGE,
                start_date: tryParseDate(draftData.time_start ?? null),
                end_date: tryParseDate(draftData.time_end ?? null),
              };
              break;
            case TIME_TYPE.UNKNOWN:
              incidentTime = {
                time_type: TIME_TYPE.UNKNOWN,
                start_date: null,
                end_date: null,
              };
              break;
            default:
              incidentTime = {
                time_type: TIME_TYPE.EXACT,
                start_date: null,
                end_date: null,
              };
          }
          setAlertProperty("incidentTimes", [incidentTime]);
        }

        // Handle associated crimes
        if (draftData.associated_crime?.length) {
          const crimes = draftData.associated_crime.map((crime) => {
            const found = crimeTypes.find((c) => c.crime === crime);
            return found || { crime, group: "" };
          });
          setAlertProperty("associatedCrimes", crimes);
        }

        // Handle contacts
        if (draftData.contacts?.length) {
          const contactsWithDefaults = draftData.contacts.map((contact) => ({
            ...contact,
            error: false,
          }));
          setAlertProperty("contacts", contactsWithDefaults);
        }

        if (draftData.attachments?.length) {
          setAlertProperty("attachments", draftData.attachments);
        }

        if (draftData.tags != null) {
          const tagDict: Record<string, ITag> = {};
          const tagsFromDraft: ITag[] = [];
          for (const [k, v] of Object.entries(draftData.tags)) {
            const tag = v as {
              id?: string;
              title?: string;
              description?: string;
              attachments?: string[];
            };
            const id = tag?.id ?? k;
            const t: ITag = {
              id,
              title: tag?.title ?? "",
              description: tag?.description ?? "",
              description_fr: "",
              attachments: tag?.attachments ?? [],
            };
            tagDict[id] = t;
            tagsFromDraft.push(t);
          }
          if (Object.keys(tagDict).length > 0) {
            setAlertProperty("tagDict", tagDict);
            setAlertProperty("tagsFromDraft", tagsFromDraft);
          }
        }

        // Clean up URL - remove token
        const newUrl = `${window.location.pathname}`;
        window.history.replaceState({}, "", newUrl);

        toast.success("Bulletin data loaded successfully");
        return;
      } catch (error) {
        console.error("Failed to load bulletin draft:", error);
        toast.error("Failed to load bulletin data. Please try again.");
        // Clean up URL on error
        window.history.replaceState({}, "", window.location.pathname);
        return;
      }
    }
  };

  // Load data on page load
  useEffect(() => {
    if (!readOnly) {
      setUsersPoliceService();
      if (contacts.length === 0) addUserEmailContact();
    }
  }, [
    readOnly,
    policeServices?.length,
    currentUser?.email,
    currentUserGroups?.length,
  ]);

  useEffect(() => {
    if (!readOnly && (mode === "add" || mode === "upload")) {
      resetState();
      // Call async function
      loadFromQueryParams().catch((error) => {
        console.error("Error loading from query params:", error);
      });
    } else {
      setCleared(true);
    }
    setUsersPoliceService();
  }, [mode, readOnly]);

  // loadFromQueryParams may run before crimeTypes resolves, leaving
  // associatedCrimes entries without a display `name` (shown as raw codes).
  // Backfill the name once crimeTypes finishes loading.
  useEffect(() => {
    if (!crimeTypes.length || !associatedCrimes.length) return;
    let changed = false;
    const enriched = associatedCrimes.map((crime) => {
      if (crime.name) return crime;
      const found = crimeTypes.find((ct) => ct.crime === crime.crime);
      if (found) {
        changed = true;
        return found;
      }
      return crime;
    });
    if (changed) setAlertProperty("associatedCrimes", enriched);
  }, [crimeTypes, associatedCrimes]);

  const showSaveDraftButton = () => {
    const shouldShowSaveDraft = mode === "add" || isDraft;
    if (shouldShowSaveDraft) {
      return (
        <Button
          variant="outlined"
          color="secondary"
          style={{ whiteSpace: "nowrap" }}
          onClick={async () => {
            try {
              await handleSaveDraft();
              toast.info(t("draft_saved"));
            } catch (err) {
              console.error("Failed to save draft:", err);
              toast.error(t("error_message_save_failed"));
            }
          }}
        >
          {t("save_draft")}
        </Button>
      );
    }
  };

  const handleCloseCancelEdit = (confirmed: boolean) => {
    setOpenCancelEdit(false);
    if (confirmed) {
      if (mode === "edit" && isDraft) {
        navigate(`/rubialert/drafts/${bulletinId}/single`);
      } else {
        navigate(`/rubialert/alerts/${bulletinId}/single`);
      }
    }
  };

  if (mode === "upload" && !bulletinId && isUploadPdfModalOpen) {
    if (readOnly)
      return (
        // <ReadOnlyDialog youtubeUrl="https://www.youtube.com/watch?v=T_KrYLW4jw8" />
        <ReadOnlyDialog />
      );

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          alignContent: "center",
          flexWrap: "wrap",
          padding: "64px",
          gap: "16px",
          maxWidth: "var(--page-max-width)",
          width: "90%",
          margin: "10px auto",
          background: colors.primary.contrastText,
          boxShadow: "var(--card-shadow)",
          borderRadius: "3px",
          position: "absolute",
          top: "40%",
          left: "50%",
          transform: "translate(-50%, -50%)",
        }}
      >
        <div>
          <UploadPdf
            setIsUploadPdfModalOpen={setIsUploadPdfModalOpen}
            setEvidenceFilesLoading={setEvidenceFilesLoading}
          />
        </div>
      </Box>
    );
  }
  if (readOnly) {
    return (
      // <ReadOnlyDialog youtubeUrl="https://www.youtube.com/watch?v=gvgBUY8iNO4" />
      <ReadOnlyDialog />
    );
  }

  return (
    <div className="alert-container">
      {mode === "edit" && (
        <div className="cancel-edit-container">
          <Button
            variant="contained"
            color="primary"
            onClick={() => setOpenCancelEdit(true)}
          >
            {t("cancel_editing")}
          </Button>
        </div>
      )}
      <div className="navigation-container">
        {steps.map((label, index) => (
          <StepNavigation
            key={index}
            index={index}
            activeStep={activeStep}
            steps={steps}
            label={label}
          />
        ))}
      </div>
      {cleared && activeStep === 0 && (
        <DetailsStep
          ref={detailsStepRef}
          policeService={policeService!}
          onPoliceServiceSelect={handleUpdate("policeService")}
          occurrenceNumbers={occurrenceNumbers}
          type={type ?? ""}
          setType={handleUpdate("type")}
          associatedCrimes={associatedCrimes}
          incidentTimes={incidentTimes}
          setIncidentTimes={handleUpdate("incidentTimes")}
          description={
            originalLanguage === "fr"
              ? (descriptionFr ?? "")
              : (description ?? "")
          }
          setDescription={handleUpdate("description")}
          updateState={updateState}
          validationAttempted={validationAttempted}
          setErrors={setErrors}
        />
      )}

      {activeStep === 1 && bulletinId && (
        <UploadStep
          bulletinID={bulletinId}
          mode={mode}
          imagesLoading={evidenceFilesLoading}
          onAttachmentSyncingChange={setIsAttachmentSyncing}
        />
      )}

      {activeStep === 2 && (
        <DisplayAlert
          mode="add"
          issuing_police_service={policeService ? policeService.name : ""}
          associatedCrimes={associatedCrimes.map((crime) => crime.crime)}
          occurrenceNumbers={occurrenceNumbers}
          issuedTime={issuedTime ? issuedTime?.toISOString() : null}
          description={description}
          pdf={pdf}
          evidences={evidence_files}
        />
      )}
      {/* BUTTONS */}
      <div className="alert-bottom-action-container">
        {activeStep > 0 && (
          <>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<ArrowBack />}
              className="mr-2"
              onClick={() => {
                handleBack();
              }}
            >
              {t("back")}
            </Button>
            {activeStep < 3 && showSaveDraftButton()}
          </>
        )}
        {activeStep === 0 && showSaveDraftButton()}
        {activeStep < 2 && (
          <Button
            variant="contained"
            color="primary"
            endIcon={<ArrowForward />}
            disabled={isAttachmentSyncing}
            onClick={() => {
              handleNext();
            }}
          >
            {t("next")}
          </Button>
        )}
        {activeStep === 2 && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              justifyContent: "center",
              gap: "8px",
              width: "100%",
            }}
          >
            <Typography variant="caption" sx={{ textAlign: "center", mt: 2 }}>
              {t("next_step")}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={
                mode === "add" || isDraft ? (
                  <AddTask />
                ) : (
                  <PublishedWithChanges />
                )
              }
              onClick={async () => {
                try {
                  await saveBulletin(false, bulletinId);
                  toast.info(
                    mode === "add" || isDraft
                      ? t("success_message")
                      : t("success_message_update"),
                  );
                  navigate(
                    `/rubialert/alerts?bulletinId=${bulletinId}&similarTo=${bulletinId}&access=true`,
                  );
                } catch (error) {
                  console.error("Failed to save bulletin:", error);
                  toast.error(t("error_message_save_failed"));
                }
              }}
            >
              {mode === "add" || isDraft ? t("finalize") : t("update_alert")}
            </Button>
          </div>
        )}
      </div>
      {/* DIALOGS */}
      <Dialog
        open={openImageCheckDialog}
        onClose={() => setOpenImageCheckDialog(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {t("would_you_like_to_add_pictures")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {t("pictures_enhance_bulletins")}
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => {
              setImageUploadedFlag(true);
              setOpenImageCheckDialog(false);
            }}
          >
            {t("skip")}
          </Button>
          <Button onClick={() => setOpenImageCheckDialog(false)} autoFocus>
            {t("add_pictures")}
          </Button>
        </DialogActions>
      </Dialog>

      <CancelEditDialog
        title={t("cancel_editing")}
        text={t("cancel_edit_text")}
        open={openCancelEdit}
        onClose={handleCloseCancelEdit}
      />

      <PotentialDuplicateWarningDialog
        open={openDuplicateDialog}
        message="There are Bulletin(s) with the same occurrence numbers; review them before proceeding."
        items={duplicateBulletins.map((bulletin) => ({
          id: String(bulletin.id),
          href: `/rubialert/alerts/${bulletin.id}/single`,
          label:
            bulletin.occurrence_number.join(", ") || `Bulletin ${bulletin.id}`,
        }))}
        cancelLabel="Cancel Current Draft"
        continueLabel="Continue Making Bulletin"
        onCancel={() => {
          setOpenDuplicateDialog(false);
          navigate(`/rubialert/alerts`);
        }}
        onContinue={() => setOpenDuplicateDialog(false)}
      />
    </div>
  );
};

/**
 * Bulletin authoring needs the Maps API for three things that read the global
 * `google` rather than a hook: the Geocoder in `saveBulletin`, the Geocoder in
 * `validateGeocodedAddresses`, and the Places autocomplete in AddressList. They
 * used to be served by a `LoadScript` at the app root that ran on every page;
 * this mounts the API for the flow that actually needs it.
 */
export const UpdateAlert = (props: UpdateAlertProps) => (
  <GoogleMapsProvider>
    <UpdateAlertContent {...props} />
  </GoogleMapsProvider>
);
